/**
 * Translates Milo's tool arguments into the marketplace's own filter shape.
 *
 * Milo and the marketplace were built with different filter models: Milo emits
 * raw min/max numbers and comma-separated vocabulary strings, while the
 * marketplace grid works from capacity buckets and string arrays. Rather than
 * maintain two sets of filter semantics, Milo publishes its filters through
 * here and Search.tsx remains the single search authority — it owns the fetch,
 * the page reset and the rendering.
 */

/** The arguments Milo's `search_spaces` tool can emit. */
export interface AgentFilters {
  name?: string;
  address?: string;
  minRate?: number | string;
  maxRate?: number | string;
  minCapacity?: number | string;
  maxCapacity?: number | string;
  /** Comma-separated vibe vocabulary. */
  tags?: string;
  /** Comma-separated feature/amenity vocabulary. */
  features?: string;
}

/** The subset of marketplace filter state Milo can drive. */
export interface MarketplaceFilterPatch {
  address: string;
  priceFrom: string;
  priceTo: string;
  minCapacity: string;
  maxCapacity: string;
  vibes: string[];
  features: string[];
  name: string;
}

const splitList = (value?: string): string[] =>
  (value ?? "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const asString = (value?: number | string): string =>
  value === undefined || value === null || value === "" ? "" : String(value);

/**
 * Milo's filter object is already cumulative — it keeps prior constraints and
 * only changes what the user asked to change — so this is a straight
 * translation, not a merge. Anything Milo did not set becomes empty, which is
 * what makes "actually, Arlington instead" replace only the location.
 *
 * Every Milo argument maps to something; none are silently dropped.
 */
export const mapAgentFiltersToMarketplace = (
  agentFilters: AgentFilters | null | undefined
): MarketplaceFilterPatch => {
  const f = agentFilters ?? {};
  return {
    address: f.address ?? "",
    priceFrom: asString(f.minRate),
    priceTo: asString(f.maxRate),
    minCapacity: asString(f.minCapacity),
    maxCapacity: asString(f.maxCapacity),
    // Milo is instructed to emit exact vocabulary strings; the backend matches
    // tags/features with an exact IN, so no normalisation is applied here —
    // silently rewriting them would hide a genuine vocabulary mismatch.
    vibes: splitList(f.tags),
    features: splitList(f.features),
    name: f.name ?? "",
  };
};

/** True when Milo has cleared everything (drives the reset-to-unfiltered path). */
export const isEmptyAgentFilter = (agentFilters: AgentFilters | null): boolean =>
  Object.values(mapAgentFiltersToMarketplace(agentFilters)).every((v) =>
    Array.isArray(v) ? v.length === 0 : v === ""
  );
